import React, { useState, useEffect } from 'react'
import {
  Table, Card, Button, Input, Space, Tag, Modal, Descriptions,
  Statistic, Row, Col, Tabs, Typography, message, Spin, Form
} from 'antd'
import {
  EyeOutlined, SearchOutlined, PhoneOutlined, UserOutlined,
  TrophyOutlined, ShoppingOutlined, DeleteOutlined, PlusOutlined, WalletOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { customerApi } from '../services/api'

const { Text } = Typography
const { TabPane } = Tabs

interface Customer {
  id: number
  phone: string
  username?: string
  nickname?: string
  points: number
  total_points: number
  balance: number
  member_level_id?: number
  memberLevel?: { id: number; name: string; color: string; discount: number }
  created_at: string
  last_signin_at?: string
}

interface CustomerDetail {
  user: Customer
  stats: {
    orderCount: number
    totalAmount: number
    points: number
    totalPoints: number
    balance: number
  }
  recentOrders: any[]
  pointsLogs: any[]
  balanceLogs: any[]
}

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [detailVisible, setDetailVisible] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetail | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  // 新建客户弹窗
  const [createVisible, setCreateVisible] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createPhone, setCreatePhone] = useState('')
  const [createNickname, setCreateNickname] = useState('')

  // 储值弹窗
  const [rechargeVisible, setRechargeVisible] = useState(false)
  const [rechargeLoading, setRechargeLoading] = useState(false)
  const [rechargeCustomerId, setRechargeCustomerId] = useState<number | null>(null)
  const [rechargeAmount, setRechargeAmount] = useState('')
  const [rechargeRemark, setRechargeRemark] = useState('')

  // 加载客户列表
  const fetchCustomers = async (page = 1) => {
    setLoading(true)
    try {
      const res = await customerApi.getCustomers({
        page,
        page_size: pagination.pageSize,
        keyword,
        type: 1 // 只查个人客户
      }) as any
      if (res.code === 200 || res.code === 0) {
        setCustomers(res.data.list || [])
        setPagination(prev => ({ ...prev, current: page, total: res.data.total || 0 }))
      }
    } catch (err) {
      message.error('加载客户列表失败')
    } finally {
      setLoading(false)
    }
  }

  // 查看客户详情
  const viewDetail = async (customerId: number) => {
    setDetailLoading(true)
    setDetailVisible(true)
    try {
      const res = await customerApi.getCustomerDetail(customerId) as any
      if (res.code === 200 || res.code === 0) {
        setSelectedCustomer(res.data)
      } else {
        message.error(res.message || '获取客户详情失败')
      }
    } catch (err) {
      message.error('获取客户详情失败')
    } finally {
      setDetailLoading(false)
    }
  }

  // 删除客户
  const handleDelete = async (customerId: number) => {
    try {
      const res = await customerApi.deleteCustomer?.(customerId) as any
      if (res?.code === 200 || res?.code === 0) {
        message.success('删除成功')
        fetchCustomers(pagination.current)
      } else {
        message.error(res?.message || '删除失败')
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || '删除失败'
      message.error(msg)
    }
  }

  // 新建客户
  const handleCreate = async () => {
    if (!createPhone) {
      message.error('请输入手机号')
      return
    }
    setCreateLoading(true)
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
      const res = await fetch('https://admin.zchef.cn/api/member/admin/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          phone: createPhone,
          nickname: createNickname || ''
        })
      }).then(r => r.json())
      if (res.code === 200 || res.code === 0) {
        message.success('客户创建成功')
        setCreateVisible(false)
        setCreatePhone('')
        setCreateNickname('')
        fetchCustomers(1)
      } else {
        message.error(res.message || '创建失败')
      }
    } catch (err) {
      message.error('创建失败')
    } finally {
      setCreateLoading(false)
    }
  }

  // 储值
  const handleRecharge = async () => {
    if (!rechargeCustomerId || !rechargeAmount || Number(rechargeAmount) <= 0) {
      message.error('请输入有效金额')
      return
    }
    setRechargeLoading(true)
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
      const res = await fetch('https://admin.zchef.cn/api/member/admin/recharge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          userId: rechargeCustomerId,
          amount: Number(rechargeAmount),
          remark: rechargeRemark || '管理端手动储值'
        })
      }).then(r => r.json())
      if (res.code === 200 || res.code === 0) {
        message.success('储值成功，余额 ¥' + Number(res.data?.balance || 0).toFixed(2))
        setRechargeVisible(false)
        setRechargeAmount('')
        setRechargeRemark('')
        fetchCustomers(pagination.current)
      } else {
        message.error(res.message || '储值失败')
      }
    } catch (err) {
      message.error('储值失败')
    } finally {
      setRechargeLoading(false)
    }
  }

  // 打开储值弹窗
  const openRecharge = (customerId: number) => {
    setRechargeCustomerId(customerId)
    setRechargeVisible(true)
  }

  useEffect(() => {
    fetchCustomers(1)
  }, [])

  // 表格列定义 — 个人客户，无企业字段
  const columns: ColumnsType<Customer> = [
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (phone) => <><PhoneOutlined /> {phone}</>
    },
    {
      title: '姓名',
      dataIndex: 'username',
      key: 'username',
      width: 100,
      render: (name, record) => name || record.nickname || '-'
    },
    {
      title: '会员等级',
      dataIndex: 'memberLevel',
      key: 'memberLevel',
      width: 100,
      render: (level) => level ? (
        <Tag color={level.color || '#55266c'}>{level.name}</Tag>
      ) : <Tag>普通会员</Tag>
    },
    {
      title: '积分',
      dataIndex: 'points',
      key: 'points',
      width: 80,
      render: (points) => <span style={{ color: '#faad14' }}>{points || 0}</span>
    },
    {
      title: '储值余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 100,
      render: (balance) => <span style={{ color: '#52c41a' }}>¥{Number(balance || 0).toFixed(2)}</span>
    },
    {
      title: '订单数',
      key: 'orderCount',
      width: 80,
      render: () => '-'
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => viewDetail(record.id)}>
            详情
          </Button>
          <Button type="link" icon={<WalletOutlined />} onClick={() => openRecharge(record.id)}>
            储值
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => {
            Modal.confirm({
              title: '确认删除该客户？',
              content: '删除后该客户所有数据将无法恢复',
              okText: '确认删除',
              okType: 'danger',
              cancelText: '取消',
              onOk: () => handleDelete(record.id)
            })
          }}>
            删除
          </Button>
        </Space>
      )
    }
  ]

  // 订单状态映射
  const orderStatusMap: Record<number, { text: string; color: string }> = {
    0: { text: '待付款', color: 'orange' },
    1: { text: '待配货', color: 'blue' },
    2: { text: '配送中', color: 'cyan' },
    3: { text: '待配送', color: 'purple' },
    4: { text: '已完成', color: 'green' },
    5: { text: '已取消', color: 'default' }
  }

  // 积分类型映射
  const pointsTypeMap: Record<number, string> = {
    1: '获得',
    2: '消费',
    3: '兑换',
    4: '过期'
  }

  // 储值类型映射
  const balanceTypeMap: Record<number, string> = {
    1: '充值',
    2: '消费',
    3: '退款'
  }

  return (
    <div style={{ padding: 24 }}>
      <Card title="个人客户管理" extra={
        <Space>
          <Input.Search
            placeholder="搜索手机号/姓名"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onSearch={() => fetchCustomers(1)}
            style={{ width: 200 }}
            enterButton={<SearchOutlined />}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
            新建客户
          </Button>
        </Space>
      }>
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page) => fetchCustomers(page)
          }}
        />
      </Card>

      {/* 新建客户弹窗 */}
      <Modal
        title="新建客户"
        open={createVisible}
        onCancel={() => {
          setCreateVisible(false)
          setCreatePhone('')
          setCreateNickname('')
        }}
        onOk={handleCreate}
        confirmLoading={createLoading}
        okText="创建"
        cancelText="取消"
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <label>手机号 <span style={{ color: 'red' }}>*</span></label>
            <Input
              placeholder="请输入手机号"
              value={createPhone}
              onChange={e => setCreatePhone(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
          <div>
            <label>姓名（可选）</label>
            <Input
              placeholder="请输入姓名"
              value={createNickname}
              onChange={e => setCreateNickname(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
        </div>
      </Modal>

      {/* 储值弹窗 */}
      <Modal
        title="手动储值"
        open={rechargeVisible}
        onCancel={() => {
          setRechargeVisible(false)
          setRechargeAmount('')
          setRechargeRemark('')
        }}
        onOk={handleRecharge}
        confirmLoading={rechargeLoading}
        okText="确认储值"
        cancelText="取消"
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <label>储值金额（元）<span style={{ color: 'red' }}>*</span></label>
            <Input
              type="number"
              placeholder="请输入金额"
              value={rechargeAmount}
              onChange={e => setRechargeAmount(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
          <div>
            <label>备注（可选）</label>
            <Input.TextArea
              placeholder="请输入备注"
              value={rechargeRemark}
              onChange={e => setRechargeRemark(e.target.value)}
              style={{ marginTop: 8 }}
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* 客户详情弹窗 */}
      <Modal
        title={<><UserOutlined /> 个人客户详情</>}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={860}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : selectedCustomer ? (
          <>
            {/* 统计概览 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="消费总额"
                    value={Number(selectedCustomer.stats.totalAmount || 0)}
                    precision={2}
                    prefix="¥"
                    valueStyle={{ color: '#55266c', fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="订单数"
                    value={selectedCustomer.stats.orderCount}
                    prefix={<ShoppingOutlined />}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="积分余额"
                    value={selectedCustomer.stats.points}
                    prefix={<TrophyOutlined />}
                    valueStyle={{ color: '#faad14', fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="储值余额"
                    value={Number(selectedCustomer.stats.balance || 0)}
                    precision={2}
                    prefix="¥"
                    valueStyle={{ color: '#52c41a', fontSize: 20 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* 基本信息 — 仅个人客户字段，无企业名称/月结 */}
            <Descriptions title="基本信息" bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="手机号">{selectedCustomer.user.phone}</Descriptions.Item>
              <Descriptions.Item label="姓名">{selectedCustomer.user.username || selectedCustomer.user.nickname || '-'}</Descriptions.Item>
              <Descriptions.Item label="会员等级">
                {selectedCustomer.user.memberLevel ? (
                  <Tag color={selectedCustomer.user.memberLevel.color || '#55266c'}>
                    {selectedCustomer.user.memberLevel.name}
                  </Tag>
                ) : <Tag>普通会员</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="累计积分">{selectedCustomer.stats.totalPoints}</Descriptions.Item>
              <Descriptions.Item label="储值余额">
                <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 16 }}>
                  ¥{Number(selectedCustomer.stats.balance || 0).toFixed(2)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="可用积分">
                <span style={{ color: '#faad14', fontWeight: 600, fontSize: 16 }}>
                  {selectedCustomer.stats.points}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="注册时间">
                {selectedCustomer.user.created_at ? new Date(selectedCustomer.user.created_at).toLocaleString('zh-CN') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最近签到">
                {selectedCustomer.user.last_signin_at
                  ? new Date(selectedCustomer.user.last_signin_at).toLocaleDateString('zh-CN')
                  : '从未签到'}
              </Descriptions.Item>
            </Descriptions>

            {/* 详情标签页：订单 + 积分明细 + 储值明细 + 交易明细 */}
            <Tabs defaultActiveKey="orders">
              <TabPane tab={`最近订单 (${selectedCustomer.recentOrders.length})`} key="orders">
                <Table
                  size="small"
                  dataSource={selectedCustomer.recentOrders}
                  rowKey="id"
                  pagination={false}
                  locale={{ emptyText: '暂无订单' }}
                  columns={[
                    { title: '订单号', dataIndex: 'order_no', key: 'order_no', width: 160 },
                    { title: '金额', dataIndex: 'final_amount', key: 'final_amount', width: 100,
                      render: (v) => `¥${Number(v || 0).toFixed(2)}` },
                    { title: '状态', dataIndex: 'status', key: 'status', width: 90,
                      render: (s) => {
                        const st = orderStatusMap[s] || { text: '未知', color: 'default' }
                        return <Tag color={st.color}>{st.text}</Tag>
                      }},
                    { title: '下单时间', dataIndex: 'created_at', key: 'created_at', width: 160,
                      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' }
                  ]}
                />
              </TabPane>

              <TabPane tab={`积分明细 (${selectedCustomer.pointsLogs.length})`} key="points">
                <Table
                  size="small"
                  dataSource={selectedCustomer.pointsLogs}
                  rowKey="id"
                  pagination={false}
                  locale={{ emptyText: '暂无积分记录' }}
                  columns={[
                    { title: '类型', dataIndex: 'type', key: 'type', width: 80,
                      render: (t) => <Tag color={t === 1 ? 'green' : t === 2 ? 'red' : 'default'}>{pointsTypeMap[t] || '未知'}</Tag> },
                    { title: '积分变动', key: 'change', width: 100,
                      render: (_, r) => <Text style={{ color: r.type === 1 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                        {r.type === 1 ? '+' : '-'}{r.points}
                      </Text> },
                    { title: '剩余积分', dataIndex: 'balance', key: 'balance', width: 90 },
                    { title: '说明', dataIndex: 'remark', key: 'remark', ellipsis: true },
                    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 160,
                      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' }
                  ]}
                />
              </TabPane>

              <TabPane tab={`储值明细 (${selectedCustomer.balanceLogs.length})`} key="balance">
                <Table
                  size="small"
                  dataSource={selectedCustomer.balanceLogs}
                  rowKey="id"
                  pagination={false}
                  locale={{ emptyText: '暂无储值记录' }}
                  columns={[
                    { title: '类型', dataIndex: 'type', key: 'type', width: 80,
                      render: (t) => <Tag color={t === 1 ? 'green' : t === 2 ? 'red' : 'blue'}>{balanceTypeMap[t] || '未知'}</Tag> },
                    { title: '金额变动', key: 'change', width: 100,
                      render: (_, r) => <Text style={{ color: r.type === 1 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                        {r.type === 1 ? '+' : '-'}¥{Number(r.amount || 0).toFixed(2)}
                      </Text> },
                    { title: '剩余余额', dataIndex: 'balance', key: 'balance', width: 100,
                      render: (v) => `¥${Number(v || 0).toFixed(2)}` },
                    { title: '说明', dataIndex: 'remark', key: 'remark', ellipsis: true },
                    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 160,
                      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' }
                  ]}
                />
              </TabPane>

              <TabPane tab="交易明细" key="transactions">
                {(() => {
                  // 合并积分和储值记录，按时间倒序排列
                  const allTransactions = [
                    ...selectedCustomer.pointsLogs.map(l => ({
                      id: `p-${l.id}`,
                      type: '积分',
                      change: l.type === 1 ? `+${l.points}` : `-${l.points}`,
                      isPositive: l.type === 1,
                      remark: l.remark || pointsTypeMap[l.type] || '',
                      time: l.created_at
                    })),
                    ...selectedCustomer.balanceLogs.map(l => ({
                      id: `b-${l.id}`,
                      type: '储值',
                      change: l.type === 1 ? `+¥${Number(l.amount || 0).toFixed(2)}` : `-¥${Number(l.amount || 0).toFixed(2)}`,
                      isPositive: l.type === 1,
                      remark: l.remark || balanceTypeMap[l.type] || '',
                      time: l.created_at
                    }))
                  ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())

                  return (
                    <Table
                      size="small"
                      dataSource={allTransactions}
                      rowKey="id"
                      pagination={false}
                      locale={{ emptyText: '暂无交易记录' }}
                      columns={[
                        { title: '类型', dataIndex: 'type', key: 'type', width: 80,
                          render: (v) => <Tag color={v === '积分' ? 'gold' : 'green'}>{v}</Tag> },
                        { title: '变动', dataIndex: 'change', key: 'change', width: 120,
                          render: (v, r) => <Text style={{ color: r.isPositive ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>{v}</Text> },
                        { title: '说明', dataIndex: 'remark', key: 'remark', ellipsis: true },
                        { title: '时间', dataIndex: 'time', key: 'time', width: 160,
                          render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' }
                      ]}
                    />
                  )
                })()}
              </TabPane>
            </Tabs>
          </>
        ) : null}
      </Modal>
    </div>
  )
}

export default Customers
